import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Logo from "../components/Logo";
import WorkflowDiagram from "../components/WorkflowDiagram";
import {
  ArrowRight, CheckCircle, Warning, Sparkle, EnvelopeSimple,
  MapTrifold, ChatCircleDots, ReceiptX, ChartLineUp, Rocket, Lightning,
} from "@phosphor-icons/react";
import { INDUSTRY_PAGES } from "./industryPagesConfig";

const ICON_MAP = {
  email: EnvelopeSimple,
  dispatch: MapTrifold,
  customer: ChatCircleDots,
  invoice: ReceiptX,
  insights: ChartLineUp,
};

// Update the document head with SEO metadata for the page (no react-helmet needed).
function useSeoHead(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", description);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = description;
      document.head.appendChild(m);
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
    };
  }, [title, description]);
}

export default function IndustryLandingPage() {
  const { slug } = useParams();
  const cfg = INDUSTRY_PAGES[slug];

  // SEO before any conditional return so hook rules are respected
  useSeoHead(
    cfg?.seo_title || "FreightPilot AI",
    cfg?.seo_description || "AI for trucking companies",
  );

  if (!cfg) return <Navigate to="/" replace />;

  const HeroIcon = cfg.icon;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid={`industry-page-${slug}`}>
      {/* Sticky header */}
      <div className="sticky top-0 z-30 border-b border-white/5 bg-[#0A0A0A]/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white">
            <Logo size={28} />
            <span className="font-display font-semibold tracking-tight">FreightPilot AI</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing" className="text-xs text-neutral-400 hover:text-white hidden sm:inline">Pricing</Link>
            <Link to="/contact" className="text-xs text-neutral-400 hover:text-white hidden sm:inline">Contact</Link>
            <Link to="/signup" data-testid="industry-header-cta"
                  className="btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5">
              Start free trial <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[720px] h-[720px] bg-[#FF5A1F]/[0.10] blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-20 text-center">
          <div className="inline-flex items-center gap-2 pill pill-primary mb-6" data-testid="industry-eyebrow">
            <HeroIcon size={14} weight="fill" /> {cfg.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-4xl mx-auto"
              data-testid="industry-headline">
            {cfg.title_lead}<br />
            <span className="text-[#FF5A1F]">{cfg.title_accent}</span><br />
            <span className="text-neutral-400 text-3xl sm:text-4xl md:text-5xl">{cfg.title_close}</span>
          </h1>
          <p className="text-neutral-400 text-base sm:text-lg mt-8 max-w-2xl mx-auto leading-relaxed"
             data-testid="industry-tagline">
            {cfg.tagline}
          </p>
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/signup" data-testid="industry-cta-primary"
                  className="btn-primary rounded-full px-6 py-3.5 text-sm inline-flex items-center gap-2">
              <Rocket size={14} weight="fill" /> {cfg.cta_primary}
            </Link>
            <Link to="/contact" data-testid="industry-cta-secondary"
                  className="rounded-full px-6 py-3.5 text-sm inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-neutral-200 hover:text-white transition-colors">
              {cfg.cta_secondary} <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Proof stat banner */}
      <section className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 -mt-4 sm:-mt-6 mb-12">
        <div className="rounded-3xl bg-gradient-to-br from-[#FF5A1F]/15 to-[#FF5A1F]/[0.03] border border-[#FF5A1F]/25 p-6 sm:p-8 text-center"
             data-testid="industry-proof">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-2">
            PROOF POINT
          </div>
          <div className="font-display text-5xl sm:text-6xl font-bold text-white tabular-nums">
            {cfg.proof_stat}
          </div>
          <div className="text-sm sm:text-base text-neutral-300 mt-1">{cfg.proof_unit}</div>
          <div className="text-xs text-neutral-500 mt-3 max-w-lg mx-auto">{cfg.proof_context}</div>
        </div>
      </section>

      {/* Pain points */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-16 sm:py-20">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 pill mb-3">
            <Warning size={12} weight="fill" className="text-yellow-500" /> IF THIS SOUNDS FAMILIAR
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            You're bleeding time on repetitive work.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {cfg.pain_points.map((p, i) => (
            <div key={i}
                 className="card-surface p-4 flex items-start gap-3"
                 data-testid={`industry-pain-${i}`}>
              <div className="w-6 h-6 rounded-full bg-red-500/10 text-red-400 flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold">
                ✕
              </div>
              <p className="text-sm text-neutral-300 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Solutions */}
      <section className="border-t border-white/5 max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 pill pill-primary mb-3">
            <Sparkle size={12} weight="fill" /> WHAT WE AUTOMATE
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            AI does the busywork.
            <br /> <span className="text-[#FF5A1F]">You do the driving decisions.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cfg.solutions.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || Sparkle;
            return (
              <div key={i}
                   className="card-surface p-5"
                   data-testid={`industry-solution-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center mb-4">
                  <Icon size={20} weight="fill" />
                </div>
                <div className="text-base font-semibold text-white mb-2">{s.title}</div>
                <p className="text-sm text-neutral-400 leading-relaxed">{s.detail}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Workflow diagram — same 5-step flow shown on main landing */}
      <div className="border-t border-white/5">
        <WorkflowDiagram />
      </div>

      {/* Testimonial placeholder */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-16 sm:py-20 text-center">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3 font-semibold">
          PILOT PROGRAM
        </div>
        <blockquote className="font-display text-2xl sm:text-3xl text-neutral-300 leading-relaxed max-w-3xl mx-auto italic">
          "{cfg.testimonial_placeholder}"
        </blockquote>
        <div className="text-xs text-neutral-500 mt-6">
          — MIKELE MANENE, Founder, FreightPilot AI
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2 pill pill-primary mb-5">
            <Lightning size={12} weight="fill" /> GET STARTED
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            Take back your evenings.
            <br /> <span className="text-[#FF5A1F]">Start free today.</span>
          </h2>
          <p className="text-neutral-400 mt-5 max-w-xl mx-auto">
            Card on file, $0 today. Cancel anytime before day 8. See the AI parse a real broker email in under 6 seconds.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/signup" data-testid="industry-cta-bottom-primary"
                  className="btn-primary rounded-full px-6 py-3.5 text-sm inline-flex items-center gap-2">
              <Rocket size={14} weight="fill" /> Start free trial
            </Link>
            <Link to="/contact" data-testid="industry-cta-bottom-secondary"
                  className="rounded-full px-6 py-3.5 text-sm inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-neutral-200 hover:text-white transition-colors">
              Book a walkthrough <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="flex items-center justify-center gap-6 mt-10 text-xs text-neutral-500 flex-wrap">
            <div className="flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-400" /> Free 7-day trial</div>
            <div className="flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-400" /> $0 today</div>
            <div className="flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-400" /> Cancel anytime</div>
          </div>
        </div>
      </section>

      {/* Cross-link to other industries */}
      <section className="border-t border-white/5 max-w-6xl mx-auto px-4 sm:px-8 py-14">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-4 font-semibold text-center">
          FREIGHTPILOT AI FOR EVERY ROLE
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {Object.values(INDUSTRY_PAGES)
            .filter((p) => p.slug !== slug)
            .map((p) => {
              const Icon = p.icon;
              return (
                <Link key={p.slug} to={`/for/${p.slug}`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 hover:border-white/25 text-neutral-400 hover:text-white px-4 py-2 text-xs transition-colors"
                      data-testid={`industry-crosslink-${p.slug}`}>
                  <Icon size={12} weight="fill" /> {p.eyebrow.replace("AI FOR ", "")}
                </Link>
              );
            })}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center text-xs text-neutral-600">
          © 2026 FreightPilot AI · <Link to="/" className="hover:text-neutral-400">Home</Link> · <Link to="/pricing" className="hover:text-neutral-400">Pricing</Link> · <Link to="/contact" className="hover:text-neutral-400">Contact</Link>
        </div>
      </footer>
    </div>
  );
}
